import React, { useContext, useMemo } from 'react';
import { ButtonV2, useDisclosure, TextV2, Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { Category } from '@graphql/generated';
import { AnalyticsContext, Link, useTranslation } from '@shared/core';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';
import { button2ToButtonsOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useScaleGuestSiteFontSize } from '@apps/guest/components/GuestSiteTypographyOverride/hooks/useScaleGuestSiteFontSize';
import { useEventDisplayName, useEventInfo } from '@shared/utils/eventInfo';
import { useColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { getButtonStylesWithAccentColor } from '@apps/guest/packages/layout-engine/layouts/layout.utils';
import { createSearchParamsFromAccommodationsPage, useHotelRoutePaths } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';
import AccommodationsConsoleDialog from '@apps/accommodations';
import { AccommodationsConsoleConfigProvider, AccommodationsConsoleConfig, MapLayoutMode } from '@apps/accommodations/AccommodationsConsoleConfigContext';
import { SortOption } from '@apps/accommodations/AccommodationsConsole.types';

type ExploreOtherOptionsProps = Readonly<{
  pageSlug: string;
}>;

export const ExploreOtherOptions: React.FC<ExploreOtherOptionsProps> = ({ pageSlug }) => {
  const analytics = useContext(AnalyticsContext);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isTravelAccommodationsConsoleV2Enabled] = useIsFeatureEnabled('travelAccommodationsConsoleV2');

  const fontPackExperimentEnabled = useFeatureValue('fontPackExperiment').value === 'treatment';
  const { scaleFontSize } = useScaleGuestSiteFontSize();
  const { accentColor } = useColorPaletteProvider();
  const buttonStyles = useMemo(() => (accentColor ? getButtonStylesWithAccentColor(accentColor) : {}), [accentColor]);

  const { tq } = useTranslation('accommodationsConsole');

  const hotelRoute = useHotelRoutePaths();

  const { eventHandle } = useEventInfo();
  const eventDisplayName = useEventDisplayName();
  const url = hotelRoute.hotelSearch.buildRelativeUrl(`${eventHandle}`, createSearchParamsFromAccommodationsPage('explore_other_options'));

  const accommodationsConsoleConfig: AccommodationsConsoleConfig = useMemo(
    () => ({
      topBar: {
        searchForm: {
          dateRangeEnabled: true,
          occupancySelectorEnabled: true,
        },
      },
      layout: {
        mapLayout: {
          mode: MapLayoutMode.FULL,
        },
      },
      map: {
        shouldShowVenueMapPin: true,
      },
      filtersPanel: {
        hideEventNameAndNote: false,
        shouldShowJoyTravelBenefitsFilter: false,
      },
      listingsPanel: {
        listingData: {
          shouldShowAllAccommodations: true,
          shouldFilterOutNonJoyRoomBlocks: false,
        },
        sortConfig: {
          sortOptions: [
            [SortOption.COUPLES_PICKS, tq`Couple's Picks`],
            [SortOption.TOP_MATCHES, tq`Top Matches`],
            [SortOption.PRICE_LOW_TO_HIGH, tq`Price Low to High`],
            [SortOption.PRICE_HIGH_TO_LOW, tq`Price High to Low`],
            [SortOption.MOST_SAVINGS, tq`Most Savings`],
            [SortOption.DISTANCE, tq`Distance`],
          ],
          defaultSortOption: SortOption.TOP_MATCHES,
        },
        listingEntry: {
          roomBlockTag: '',
          shouldShowCouplePicksTag: true,
          shouldShowAddedTag: false,
          shouldShowRoomBlockNoteAndDate: true,
          bookingCta: {
            default: {
              buttonTitle: tq`See Availability`,
              buttonProps: {
                variant: 'solid',
                intent: 'newPrimary',
                _hover: { backgroundColor: { _: 'newUIBrandPrimary', md: 'buttonNewPrimaryHover' } },
              },
            },
          },
        },
      },
    }),
    [tq],
  );

  const handleOnClick = useEventCallback(() => {
    if (isTravelAccommodationsConsoleV2Enabled) {
      onOpen();
      return;
    }

    analytics.track({
      action: 'LinkInteracted',
      category: 'linkify',
      label: 'accommodations',
      extraInfo: {
        url,
        actionType: 'click',
        source: 'accommodations',
      },
    });
    analytics.track({
      action: 'BookingAssistantClicked',
      actionType: 'click',
      category: 'wedding',
      pagePrefix: 'guest',
      page: pageSlug,
    });
  });

  return (
    <>
      <GuestSiteTypographyOverride
        override={button2ToButtonsOverride}
        defaultFontFamily="Inter UI"
      >
        <Flex
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={6}
          paddingX="24px"
        >
          <TextV2
            typographyVariant="body3"
            color={accentColor}
            fontSize="14px"
          >
            {tq`Browse more hotels with our special rates`}
          </TextV2>
          <ButtonV2
            width={pxToRem(244)}
            typographyVariant="button2"
            intent="neutral"
            shape="rounded"
            variant="outline"
            fontWeight={600}
            fontSize={scaleFontSize(pxToRem(16), Category.BUTTONS)}
            {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
            onClick={handleOnClick}
            {...(!isTravelAccommodationsConsoleV2Enabled && { as: Link, to: url })}
            {...buttonStyles}
          >
            {tq`Explore More Hotels`}
          </ButtonV2>
        </Flex>
      </GuestSiteTypographyOverride>
      <AccommodationsConsoleConfigProvider config={accommodationsConsoleConfig}>
        <AccommodationsConsoleDialog
          isOpen={isOpen}
          onClose={onClose}
          eventHandle={eventHandle}
          viewMode="overlay"
          eventName={eventDisplayName}
        />
      </AccommodationsConsoleConfigProvider>
    </>
  );
};
